import React from 'react';
import {
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  SearchX,
} from 'lucide-react';
import { absenceBarStyle } from '../../utils/absenceBarColors';
import { useHorizontalWheelScroll } from '../../hooks/useHorizontalWheelScroll';
import {
  assigneesLabel,
  crewLabel,
  effectiveActivityAddress,
  formatListEnd,
  formatListStart,
  getTypeLabel,
  projectLabel,
  schedulingStatusLabel,
  schedulingStatusTextClass,
  type ActivityListRow,
  type ActivityTypeOption,
  type ListSortColumn,
} from './activityListFormat';

export type { ActivityListRow, ListSortColumn };

type ActivityListProps = {
  items: ActivityListRow[];
  pageItems: ActivityListRow[];
  activityTypes: ActivityTypeOption[];
  timeZone: string;
  sortColumn: ListSortColumn;
  sortDir: 'asc' | 'desc';
  onSort: (column: ListSortColumn) => void;
  selectedIds: Set<string>;
  onToggleSelected: (id: string, selected: boolean) => void;
  onToggleAll: (selected: boolean) => void;
  onOpenItem: (activity: ActivityListRow) => void;
  hasActiveFilters: boolean;
  onResetFilters?: () => void;
  onCreateActivity?: () => void;
  pageIndex: number;
  pageCount: number;
  rangeFrom: number;
  rangeTo: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
};

const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && Boolean(target.closest('button, input, a, [role="menu"]'));

function ListSortableHeader({
  label,
  column,
  activeColumn,
  dir,
  onSort,
}: {
  label: string;
  column: ListSortColumn;
  activeColumn: ListSortColumn;
  dir: 'asc' | 'desc';
  onSort: (column: ListSortColumn) => void;
}) {
  const active = activeColumn === column;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-4 py-2.5 text-left align-middle whitespace-nowrap"
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`group/sort inline-flex max-w-full items-center gap-1 text-xs font-semibold transition-colors ${
          active ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'
        }`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

function StatusCell({ activity }: { activity: ActivityListRow }) {
  const isAbsence = activity.list_kind === 'absence';
  if (isAbsence) return <span className="text-slate-400">—</span>;
  return (
    <span className={`text-xs ${schedulingStatusTextClass(activity.scheduling_status, activity.done)}`}>
      {schedulingStatusLabel(activity.scheduling_status, activity.done)}
    </span>
  );
}

function TypeCell({
  activity,
  activityTypes,
}: {
  activity: ActivityListRow;
  activityTypes: ActivityTypeOption[];
}) {
  const typeLabel = getTypeLabel(activity.activity_type, activityTypes);
  const absenceStyle = activity.absence_kind ? absenceBarStyle(activity.absence_kind) : null;
  if (absenceStyle) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-app-text-muted">
        <span
          className="h-2 w-2 shrink-0 rounded-full border"
          style={{ background: absenceStyle.bg, borderColor: absenceStyle.border }}
          aria-hidden
        />
        {typeLabel}
      </span>
    );
  }
  return <span className="text-xs text-app-text-muted">{typeLabel}</span>;
}

export function ActivityList({
  items,
  pageItems,
  activityTypes,
  timeZone,
  sortColumn,
  sortDir,
  onSort,
  selectedIds,
  onToggleSelected,
  onToggleAll,
  onOpenItem,
  hasActiveFilters,
  onResetFilters,
  onCreateActivity,
  pageIndex,
  pageCount,
  rangeFrom,
  rangeTo,
  pageSize,
  onPageChange,
  scrollRef,
}: ActivityListProps) {
  const selectAllRef = React.useRef<HTMLInputElement>(null);
  const internalScrollRef = React.useRef<HTMLDivElement>(null);
  const listScrollRef = scrollRef ?? internalScrollRef;
  // Die Tabelle ist breiter als der Viewport — Shift+Rad ersetzt das Ziehen der Leiste.
  const setListScrollNode = useHorizontalWheelScroll(listScrollRef);

  // Select-all über die gesamte gefilterte Menge — nicht nur die aktuelle Seite.
  const selectedCount = items.filter((row) => selectedIds.has(row.id)).length;
  const allSelected = items.length > 0 && selectedCount === items.length;

  React.useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected;
    }
  }, [selectedCount, allSelected]);

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {hasActiveFilters ? (
        <>
          <SearchX className="h-8 w-8 text-slate-300" aria-hidden />
          <div>
            <p className="text-sm font-medium text-app-text">Keine Einträge gefunden</p>
            <p className="mt-1 text-sm text-app-text-subtle">
              Für diese Suche und Filter gibt es keine Treffer.
            </p>
          </div>
          {onResetFilters ? (
            <button
              type="button"
              onClick={onResetFilters}
              className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted transition-colors hover:bg-app-hover"
            >
              Filter zurücksetzen
            </button>
          ) : null}
        </>
      ) : (
        <>
          <CalendarPlus className="h-8 w-8 text-slate-300" aria-hidden />
          <div>
            <p className="text-sm font-medium text-app-text">Noch keine Aktivitäten</p>
            <p className="mt-1 text-sm text-app-text-subtle">
              Lege deine erste Aktivität an, um den Überblick zu behalten.
            </p>
          </div>
          {onCreateActivity ? (
            <button
              type="button"
              onClick={onCreateActivity}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]"
            >
              Aktivität anlegen
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* Mobile cards */}
      <div className="app-scrollbar min-h-0 flex-1 divide-y divide-app-border overflow-y-auto overscroll-contain md:hidden">
        {items.length === 0 ? (
          emptyState
        ) : (
          pageItems.map((activity) => {
            const isAbsence = activity.list_kind === 'absence';
            const startLabel = formatListStart(activity.due_at, isAbsence, timeZone);
            const endLabel = formatListEnd(activity.due_at, activity.end_at, isAbsence, timeZone);
            const isSelected = selectedIds.has(activity.id);
            return (
              <div
                key={activity.id}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  if (isInteractiveTarget(e.target)) return;
                  onOpenItem(activity);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenItem(activity);
                  }
                }}
                className="flex items-start gap-3 px-4 py-3 active:bg-app-surface-muted"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => onToggleSelected(activity.id, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1 h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
                  aria-label={`${activity.title} auswählen`}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-app-text-muted">{activity.title}</p>
                  <p className="mt-0.5 truncate text-xs text-app-text-subtle">{projectLabel(activity)}</p>
                  <p className="mt-1 text-xs tabular-nums text-app-text-subtle">
                    {startLabel}
                    {endLabel !== '—' ? ` – ${endLabel}` : ''}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-app-text-subtle">
                    <TypeCell activity={activity} activityTypes={activityTypes} />
                    {!isAbsence ? (
                      <>
                        <span aria-hidden>·</span>
                        <StatusCell activity={activity} />
                      </>
                    ) : null}
                  </div>
                  {crewLabel(activity) !== '—' ? (
                    <p className="mt-1 truncate text-xs text-slate-400">{crewLabel(activity)}</p>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <div
        ref={setListScrollNode}
        className={`app-scrollbar hidden min-h-0 flex-1 overflow-auto overscroll-contain md:block ${
          selectedIds.size > 0 ? 'md:pb-16' : ''
        }`}
      >
        {items.length === 0 ? (
          emptyState
        ) : (
          <table className="w-full min-w-[1140px] table-fixed border-collapse text-left">
            <colgroup>
              <col className="w-10" />
              <col className="w-[148px]" />
              <col className="w-[148px]" />
              <col />
              <col className="w-[14%]" />
              <col className="w-[14%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[110px]" />
              <col className="w-[110px]" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 align-middle"
                >
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => onToggleAll(e.target.checked)}
                    className="h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
                    aria-label="Alle Einträge auswählen"
                  />
                </th>
                <ListSortableHeader
                  label="Start"
                  column="start"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Ende"
                  column="ende"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Aktivität"
                  column="aktivitaet"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Projekt"
                  column="projekt"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Adresse"
                  column="adresse"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Projektleiter"
                  column="benutzer"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Einsatzteam"
                  column="einsatzteam"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Typ"
                  column="typ"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
                <ListSortableHeader
                  label="Status"
                  column="status"
                  activeColumn={sortColumn}
                  dir={sortDir}
                  onSort={onSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-app-border">
              {pageItems.map((activity) => {
                const isAbsence = activity.list_kind === 'absence';
                const startLabel = formatListStart(activity.due_at, isAbsence, timeZone);
                const endLabel = formatListEnd(activity.due_at, activity.end_at, isAbsence, timeZone);
                const isSelected = selectedIds.has(activity.id);
                return (
                  <tr
                    key={activity.id}
                    className={`cursor-pointer transition-colors hover:bg-app-hover/80 ${
                      isSelected ? 'bg-pipedrive-blue/[0.04]' : ''
                    }`}
                    onClick={(e) => {
                      if (isInteractiveTarget(e.target)) return;
                      onOpenItem(activity);
                    }}
                  >
                    <td className="px-3 py-0 align-middle" style={{ height: 56 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => onToggleSelected(activity.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30"
                        aria-label={`${activity.title} auswählen`}
                      />
                    </td>
                    <td className="px-4 text-xs text-app-text-muted whitespace-nowrap">
                      <span className="leading-snug">{startLabel}</span>
                    </td>
                    <td className="px-4 text-xs text-app-text-muted whitespace-nowrap">
                      <span className="leading-snug">{endLabel}</span>
                    </td>
                    <td
                      className="truncate px-4 text-xs text-app-text-muted"
                      title={activity.title}
                    >
                      {activity.title}
                    </td>
                    <td className="truncate px-4 text-xs text-app-text-muted">{projectLabel(activity)}</td>
                    <td
                      className="truncate px-4 text-xs text-app-text-muted"
                      title={effectiveActivityAddress(activity) ?? undefined}
                    >
                      {effectiveActivityAddress(activity) ?? '—'}
                    </td>
                    <td className="truncate px-4 text-xs text-app-text-muted">{assigneesLabel(activity)}</td>
                    <td className="truncate px-4 text-xs text-app-text-muted">{crewLabel(activity)}</td>
                    <td className="px-4 text-xs">
                      <TypeCell activity={activity} activityTypes={activityTypes} />
                    </td>
                    <td className="px-4 text-xs">
                      <StatusCell activity={activity} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {items.length > pageSize ? (
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-app-border bg-app-surface-muted/80 px-4 py-2.5 text-xs text-app-text-muted">
          <span className="tabular-nums">
            {rangeFrom}–{rangeTo} von {items.length} Einträgen · Seite {pageIndex + 1} / {pageCount}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pageIndex <= 0}
              onClick={() => onPageChange(Math.max(0, pageIndex - 1))}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
              Zurück
            </button>
            <button
              type="button"
              disabled={pageIndex >= pageCount - 1}
              onClick={() => onPageChange(Math.min(pageCount - 1, pageIndex + 1))}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-app-border bg-app-surface px-2.5 text-sm font-medium text-app-text-muted hover:bg-app-hover disabled:pointer-events-none disabled:opacity-40"
            >
              Weiter
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
